import styled from 'styled-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import Card from '../../components/Card';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../context/AuthProvider';
import {
  formatOrgInviteRolePhrase,
  getOrgInviteNotificationMeta,
} from '../../lib/orgInvites';
import type { AppDispatch } from '../../store';
import { openTicketPortal } from '../../store/features/ticketPortal/ticketPortalSlice';

const modalSurface =
  'linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96))';
const modalBorder = 'rgba(255,255,255,.06)';
const modalText = '#E8EAED';
const modalMuted = 'rgba(186, 198, 224, 0.82)';
const modalRow = 'rgba(255, 255, 255, 0.03)';
const modalRowHover = 'rgba(255, 255, 255, 0.045)';
const modalAccent = 'rgba(224, 161, 94, 0.92)';

type NotifType =
  | 'ticket_assigned'
  | 'ticket_status'
  | 'ticket_comment'
  | 'ticket_updated'
  | 'org_invite'
  | 'org_member_joined'
  | 'meeting_scheduled';

type Notification = {
  id: string;
  type: NotifType;
  ticketId?: string | null;
  ticketNumber?: string | null;
  data?: Record<string, unknown> | null;
  orgId?: string | null;
  actorId?: string | null;
  actorRole?: 'client' | 'agent' | 'admin';
  title?: string | null;
  body?: string | null;
  link?: string | null;
  createdAt: number | string | { _seconds?: number; seconds?: number };
  read?: boolean;
};

const API_LIST = '/api/notifications?limit=100';
const API_ACK = '/api/notifications/ack';
const API_DELETE = '/api/notifications/delete';

const CardFrame = styled(Card)`
  width: 420px;
  max-width: 100%;
  flex: 0 0 420px;
  place-self: start;

  @media (max-width: 520px) {
    width: 100%;
    flex-basis: auto;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
`;

const Title = styled.h3`
  margin: 0;
  font-weight: 800;
  color: #e8eaed;
`;

const CardHint = styled.p`
  margin: 0.7rem 0 0;
  color: #9aa4b2;
  font-size: 0.92rem;
`;

const BellTrigger = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: #cbd2e1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.2rem 0.1rem;

  &:hover {
    color: #e8eaed;
  }
`;

const BellCount = styled.span`
  min-width: 1.25rem;
  height: 1.25rem;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-weight: 900;
  font-size: 0.74rem;
  color: #111827;
  background: linear-gradient(90deg, #8deBff 0%, #f8c9a5 100%);
`;

const ModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  background: rgba(8, 12, 22, 0.72);
  backdrop-filter: blur(4px);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 140ms ease;
  z-index: 1200;
`;

const ModalPanel = styled.section<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  margin: auto;
  width: min(820px, calc(100vw - 2rem));
  height: min(78vh, 760px);
  border-radius: 16px;
  border: 1px solid ${modalBorder};
  background: ${modalSurface};
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.32);
  color: ${modalText};
  z-index: 1201;
  display: grid;
  grid-template-rows: auto 1fr;
  overflow: hidden;
  transform: ${({ $open }) => ($open ? 'translateY(0) scale(1)' : 'translateY(10px) scale(0.985)')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: transform 150ms ease, opacity 150ms ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid ${modalBorder};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.02rem;
  font-weight: 900;
  color: ${modalText};
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
`;

const IconAction = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: ${modalRow};
  color: ${modalMuted};
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;

  &:hover {
    color: ${modalText};
    background: ${modalRowHover};
    border-color: ${modalAccent};
  }

  &:disabled {
    opacity: 0.58;
    cursor: default;
  }
`;

const GhostAction = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: ${modalRow};
  color: ${modalMuted};
  border-radius: 10px;
  padding: 0.5rem 0.7rem;
  font-weight: 800;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;

  &:hover {
    color: ${modalText};
    background: ${modalRowHover};
    border-color: ${modalAccent};
  }

  &:disabled {
    opacity: 0.58;
    cursor: default;
  }
`;

const FilterToggle = styled.div`
  display: inline-grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.25rem;
  padding: 0.22rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.03);
`;

const FilterOption = styled.button<{ $active?: boolean }>`
  appearance: none;
  border: 0;
  text-align: center;
  border-radius: 999px;
  padding: 0.42rem 0.82rem;
  cursor: pointer;
  font-weight: 800;
  font-size: 0.85rem;
  color: ${({ $active }) => ($active ? '#111827' : modalMuted)};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(90deg, #8debff 0%, #f8c9a5 100%)'
      : 'transparent'};

  &:hover {
    background: ${({ $active }) =>
      $active
        ? 'linear-gradient(90deg, #8debff 0%, #f8c9a5 100%)'
        : modalRowHover};
    color: ${({ $active }) => ($active ? '#111827' : modalText)};
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 0.65rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  scrollbar-width: thin;
  scrollbar-color: ${modalAccent} rgba(255, 255, 255, 0.03);

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${modalAccent};
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.04);
  }
`;

const Empty = styled.div`
  color: ${modalMuted};
  padding: 0.6rem;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Item = styled.li<{ $unread?: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.6rem;
  background: ${({ $unread }) => ($unread ? 'rgba(255, 255, 255, 0.04)' : modalRow)};
  border: 1px solid ${({ $unread }) => ($unread ? 'rgba(224, 161, 94, 0.26)' : 'rgba(255,255,255,.08)')};
  border-radius: 12px;
  padding: 0.78rem 0.85rem 0.78rem 0.95rem;
  color: ${modalText};
  line-height: 1.2;
  overflow: hidden;
  transition: background 140ms ease, border-color 140ms ease, transform 140ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    bottom: 10px;
    width: 3px;
    border-radius: 999px;
    opacity: ${({ $unread }) => ($unread ? 1 : 0)};
    background: linear-gradient(180deg, rgba(224, 161, 94, 1), rgba(94, 212, 255, 0.92));
  }

  &:hover {
    background: ${modalRowHover};
    border-color: ${({ $unread }) => ($unread ? modalAccent : 'rgba(255,255,255,.12)')};
    transform: translateY(-1px);
  }
`;

const Dot = styled.span<{ color?: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ color }) => color || '#c7d2fe'};
  margin-top: 4px;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.02);
`;

const TextWrap = styled.div`
  display: grid;
  gap: 0.15rem;
  min-width: 0;

  strong {
    font-weight: 800;
    color: ${modalText};
    font-size: 13.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    color: rgba(210, 220, 236, 0.84);
    font-size: 12.5px;
    line-height: 1.35;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const Right = styled.div`
  display: grid;
  gap: 6px;
  justify-items: end;
`;

const Meta = styled.span`
  color: rgba(186, 198, 224, 0.74);
  font-size: 0.8rem;
`;

const RowButtons = styled.div`
  display: flex;
  gap: 6px;
`;

const MarkBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: ${modalRow};
  color: ${modalMuted};
  font-weight: 800;
  border-radius: 999px;
  padding: 0.38rem 0.72rem;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;

  &:hover {
    background: ${modalRowHover};
    border-color: ${modalAccent};
    color: ${modalText};
  }
`;

const OpenBtn = styled.button`
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0.18);
  background: linear-gradient(90deg, #8deBff 0%, #f8c9a5 100%);
  color: #111827;
  font-weight: 900;
  border-radius: 999px;
  padding: 0.38rem 0.82rem;
  cursor: pointer;
  transition: transform 140ms ease, box-shadow 140ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 18px rgba(0, 0, 0, 0.18);
  }
`;

const RejectBtn = styled(MarkBtn)`
  color: #f5b7b7;
  border-color: rgba(252, 165, 165, 0.22);

  &:hover {
    border-color: rgba(252, 165, 165, 0.42);
    color: #ffd2d2;
  }
`;

const InviteLogo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: ${modalRow};
  display: grid;
  place-items: center;
  color: ${modalText};
  font-size: 0.78rem;
  font-weight: 900;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

function toDate(v: Notification['createdAt']): Date {
  if (typeof v === 'number') return new Date(v < 2e10 ? v * 1000 : v);
  if (typeof v === 'string') return new Date(v);
  const sec = v?.seconds ?? v?._seconds;
  return sec ? new Date(sec * 1000) : new Date();
}

function timeAgo(d: Date): string {
  const ms = Date.now() - d.getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 7) return `${dd}d ago`;
  return d.toLocaleDateString();
}

const COLOR_BY_TYPE: Record<NotifType, string> = {
  ticket_assigned: '#8deBff',
  ticket_status: '#f8c9a5',
  ticket_comment: '#c7d2fe',
  ticket_updated: '#9ae6b4',
  org_invite: '#8deBff',
  org_member_joined: '#c7d2fe',
  meeting_scheduled: '#f8c9a5',
};

function labelFor(n: Notification): { title: string; body?: string } {
  const fallbackNumber = String((n.data as any)?.ticketNumber ?? '').trim();
  const ticketNumber = String(n.ticketNumber ?? '').trim() || fallbackNumber;
  const ticketRef = ticketNumber ? `#${ticketNumber}` : null;
  const withTicketRef = (raw: string) => {
    const base = String(raw ?? '').trim();
    if (!ticketRef) return base;
    if (base.includes(ticketRef)) return base;
    if (/#\d{6,}/.test(base)) return base;
    return `${ticketRef} ${base}`.trim();
  };
  if (n.title) return { title: withTicketRef(n.title), body: n.body ?? undefined };
  const fallbackRef = ticketRef ?? n.ticketId ?? '';
  switch (n.type) {
    case 'ticket_assigned':
      return { title: `Agent assigned to ${fallbackRef}`.trim() };
    case 'ticket_status':
      return { title: `${fallbackRef} status updated`.trim(), body: n.body ?? undefined };
    case 'ticket_comment':
      return { title: `New comment on ${fallbackRef}`.trim(), body: n.body ?? undefined };
    case 'ticket_updated':
      return { title: `${fallbackRef} updated`.trim() };
    case 'org_invite':
      {
        const inviteMeta = getOrgInviteNotificationMeta(n);
      return {
        title: n.title || `Invitation to join ${String((n.data as any)?.businessName ?? 'organization')}`,
        body:
          `${String((n.data as any)?.primaryAccountHolderName ?? 'A primary account holder')} invited you to join this organization as ${formatOrgInviteRolePhrase(inviteMeta?.invitedRole ?? 'member')}.`,
      };
      }
    case 'org_member_joined':
      return {
        title: n.title || 'New organization member joined',
        body: n.body ?? undefined,
      };
    case 'meeting_scheduled':
      return {
        title: n.title || 'Meeting scheduled',
        body: n.body ?? undefined,
      };
    default:
      return { title: 'Notification' };
  }
}

type NotificationsCardProps = { onOpenTicket?: (id: string) => void };
type NotificationsModalProps = {
  open: boolean;
  onClose: () => void;
  onOpenTicket?: (id: string) => void;
};

export function NotificationsModal({
  open,
  onClose,
  onOpenTicket,
}: NotificationsModalProps) {
  const nav = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { role, refreshRole } = useAuth();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [ackBusy, setAckBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<'unread' | 'read'>('unread');
  const timerRef = useRef<number | null>(null);

  const emitUpdated = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('notifications:updated'));
    } catch {}
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch<any>(API_LIST);
      const arr: Notification[] = Array.isArray(res) ? res : (res?.items ?? res?.notifications ?? []);
      arr.sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
      setItems(arr);
    } catch (e) {
      console.error('[NotificationsCard] fetch failed', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return;
      try {
        setAckBusy(true);
        await apiFetch(API_ACK, { method: 'POST', body: JSON.stringify({ ids }) });
        setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n)));
      } catch (e) {
        console.error('[NotificationsCard] ack failed', e);
      } finally {
        setAckBusy(false);
        emitUpdated();
      }
    },
    [emitUpdated],
  );

  const markAll = useCallback(() => {
    const unreadIds = items.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length) {
      void markRead(unreadIds);
    }
  }, [items, markRead]);

  const deleteArchived = useCallback(async () => {
    const archivedCount = items.filter((n) => Boolean(n.read)).length;
    if (!archivedCount) return;
    try {
      setDeleteBusy(true);
      await apiFetch(API_DELETE, {
        method: 'POST',
        body: JSON.stringify({ archived: true }),
      });
      setItems((prev) => prev.filter((n) => !n.read));
    } catch (e) {
      console.error('[NotificationsCard] delete archived failed', e);
    } finally {
      setDeleteBusy(false);
      emitUpdated();
    }
  }, [emitUpdated, items]);

  const onOpen = useCallback(
    (n: Notification) => {
      void markRead([n.id]);
      onClose();
      const ticketRouteBase = role === 'client' ? '/dashboard/tickets' : '/dashboard/list';
      if (n.ticketId && onOpenTicket) {
        onOpenTicket(n.ticketId);
        return;
      }
      if (n.ticketId) {
        try { window.dispatchEvent(new CustomEvent('modals:close-all')); } catch {}
        if (role === 'admin' || role === 'agent') {
          dispatch(openTicketPortal({ ticketId: n.ticketId }));
        } else {
          nav(`${ticketRouteBase}?focusTicket=${encodeURIComponent(n.ticketId)}`);
        }
      } else if (n.link) {
        try { window.dispatchEvent(new CustomEvent('modals:close-all')); } catch {}
        nav(n.link);
      } else {
        try { window.dispatchEvent(new CustomEvent('modals:close-all')); } catch {}
        nav(ticketRouteBase);
      }
    },
    [dispatch, markRead, nav, onClose, onOpenTicket, role],
  );

  const respondToInvite = useCallback(
    async (notification: Notification, action: 'accept' | 'reject') => {
      const meta = getOrgInviteNotificationMeta(notification);
      if (!meta) return;
      try {
        setInviteBusyId(notification.id);
        await apiFetch(`/api/invites/${action}`, {
          method: 'POST',
          body: JSON.stringify({
            orgId: meta.orgId,
            token: meta.token,
          }),
        });
        setItems((prev) =>
          prev.map((item) =>
            item.id === notification.id
              ? {
                  ...item,
                  read: true,
                  data: {
                    ...(item.data ?? {}),
                    inviteStatus: action === 'accept' ? 'accepted' : 'rejected',
                  },
                }
              : item,
          ),
        );
        if (action === 'accept') {
          await refreshRole();
        }
      } catch (error) {
        console.error(`[NotificationsCard] invite ${action} failed`, error);
      } finally {
        setInviteBusyId(null);
        emitUpdated();
      }
    },
    [emitUpdated, refreshRole],
  );

  useEffect(() => {
    if (!open) return;
    setFilterMode('unread');
    void load();
    timerRef.current = window.setInterval(() => {
      void load();
    }, 15000);
    const onUpdated = () => {
      void load();
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('notifications:updated', onUpdated);
    window.addEventListener('keydown', onEsc);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      window.removeEventListener('notifications:updated', onUpdated);
      window.removeEventListener('keydown', onEsc);
    };
  }, [load, onClose, open]);

  const archivedCount = useMemo(() => items.filter((n) => Boolean(n.read)).length, [items]);
  const totalCount = items.length;
  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);
  const filteredItems = useMemo(
    () => items.filter((item) => (filterMode === 'unread' ? !item.read : Boolean(item.read))),
    [filterMode, items],
  );

  return (
    <>
      <ModalBackdrop
        type="button"
        aria-label="Close notifications modal"
        $open={open}
        onClick={onClose}
      />
      <ModalPanel $open={open} role="dialog" aria-modal="true" aria-labelledby="notifications-modal-title">
        <ModalHeader>
          <ModalTitle id="notifications-modal-title">
            Notifications{totalCount ? ` - ${totalCount}` : ''}
          </ModalTitle>
          <HeaderActions>
            <FilterToggle role="tablist" aria-label="Notification filter">
              <FilterOption
                type="button"
                role="tab"
                aria-selected={filterMode === 'unread'}
                $active={filterMode === 'unread'}
                onClick={() => setFilterMode('unread')}
              >
                Unread
              </FilterOption>
              <FilterOption
                type="button"
                role="tab"
                aria-selected={filterMode === 'read'}
                $active={filterMode === 'read'}
                onClick={() => setFilterMode('read')}
              >
                Read
              </FilterOption>
            </FilterToggle>
            {filterMode === 'unread' ? (
              <GhostAction
                type="button"
                onClick={markAll}
                disabled={ackBusy || unreadCount === 0}
              >
                Mark all read
              </GhostAction>
            ) : (
              <GhostAction
                type="button"
                onClick={() => void deleteArchived()}
                disabled={deleteBusy || archivedCount === 0}
              >
                Delete read
              </GhostAction>
            )}
            <IconAction
              type="button"
              onClick={onClose}
              aria-label="Close notifications modal"
            >
              <X size={16} />
            </IconAction>
          </HeaderActions>
        </ModalHeader>
        <ModalBody>
          {loading && filteredItems.length === 0 ? (
            <Empty>Loading...</Empty>
          ) : filteredItems.length === 0 ? (
            <Empty>
              {filterMode === 'unread'
                ? 'No unread notifications.'
                : 'No read notifications.'}
            </Empty>
          ) : (
            <List>
              {filteredItems.map((n) => {
                const { title, body } = labelFor(n);
                const inviteMeta = getOrgInviteNotificationMeta(n);
                const showInviteActions =
                  Boolean(inviteMeta) &&
                  (inviteMeta?.inviteStatus === null || inviteMeta?.inviteStatus === 'pending');
                return (
                  <Item key={n.id} $unread={!n.read}>
                    {inviteMeta ? (
                      <InviteLogo aria-hidden="true">
                        {inviteMeta.logoURL ? (
                          <img src={inviteMeta.logoURL} alt="" />
                        ) : (
                          (inviteMeta.businessName || 'O').slice(0, 1).toUpperCase()
                        )}
                      </InviteLogo>
                    ) : (
                      <Dot color={COLOR_BY_TYPE[n.type] || '#c7d2fe'} />
                    )}
                    <TextWrap>
                      <strong title={title}>{title}</strong>
                      {body ? <span title={body}>{body}</span> : null}
                    </TextWrap>
                    <Right>
                      <Meta>{timeAgo(toDate(n.createdAt))}</Meta>
                      <RowButtons>
                        {showInviteActions ? (
                          <>
                            <RejectBtn
                              type="button"
                              onClick={() => void respondToInvite(n, 'reject')}
                              disabled={inviteBusyId === n.id}
                            >
                              {inviteBusyId === n.id ? 'Working...' : 'Reject'}
                            </RejectBtn>
                            <OpenBtn
                              type="button"
                              onClick={() => void respondToInvite(n, 'accept')}
                              disabled={inviteBusyId === n.id}
                            >
                              {inviteBusyId === n.id ? 'Working...' : 'Accept'}
                            </OpenBtn>
                          </>
                        ) : !n.read ? (
                          <MarkBtn type="button" onClick={() => void markRead([n.id])}>
                            Mark read
                          </MarkBtn>
                        ) : null}
                        {!showInviteActions ? (
                          <OpenBtn type="button" onClick={() => onOpen(n)}>
                            Open
                          </OpenBtn>
                        ) : null}
                      </RowButtons>
                    </Right>
                  </Item>
                );
              })}
            </List>
          )}
        </ModalBody>
      </ModalPanel>
    </>
  );
}

export default function NotificationsCard({ onOpenTicket }: NotificationsCardProps) {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiFetch<any>(API_LIST);
      const arr: Notification[] = Array.isArray(res) ? res : (res?.items ?? res?.notifications ?? []);
      arr.sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
      setItems(arr);
    } catch (e) {
      console.error('[NotificationsCard] fetch failed', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    timerRef.current = window.setInterval(() => {
      void load();
    }, 15000);
    const onUpdated = () => {
      void load();
    };
    window.addEventListener('notifications:updated', onUpdated);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      window.removeEventListener('notifications:updated', onUpdated);
    };
  }, [load]);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);
  const totalCount = items.length;

  return (
    <>
      <CardFrame>
        <TitleRow>
          <Title>Notifications</Title>
          <BellTrigger
            type="button"
            aria-label={`Open notifications${totalCount ? `, ${totalCount} total` : ''}`}
            onClick={() => {
              setModalOpen(true);
            }}
          >
            <Bell size={18} />
            <BellCount>{totalCount}</BellCount>
          </BellTrigger>
        </TitleRow>
        <CardHint>
          {loading ? 'Loading notifications...' : `${unreadCount} unread notifications. Click the bell to view all.`}
        </CardHint>
      </CardFrame>

      <NotificationsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        onOpenTicket={onOpenTicket}
      />
    </>
  );
}
